import React, { useState, useEffect } from 'react';

// Define common options for checkboxes
const SPECIALIZATIONS_OPTIONS = [
  'Anxiety', 'Depression', 'PTSD/Trauma', 'ADHD', 'Addiction/Substance Use',
  'Eating Disorders', 'Relationship Issues', 'Family Therapy', 'Grief/Loss',
  'Bipolar Disorder', 'OCD', 'Social Anxiety', 'Panic Disorder', 'Phobias',
  'Anger Management', 'Self-Esteem', 'Life Transitions', 'Chronic Illness',
  'Sleep Disorders', 'Stress Management', 'Career Counseling', 'LGBTQ+ Issues',
  'Women\'s Issues', 'Men\'s Issues', 'Teen/Adolescent Issues', 'Parenting Support',
  'Divorce/Separation', 'Domestic Violence', 'Sexual Issues', 'Spiritual/Religious Issues'
];

const TREATMENT_APPROACHES_OPTIONS = [
  'Cognitive Behavioral Therapy (CBT)', 'Dialectical Behavior Therapy (DBT)',
  'Eye Movement Desensitization and Reprocessing (EMDR)', 'Psychodynamic Therapy',
  'Acceptance and Commitment Therapy (ACT)', 'Mindfulness-Based Therapy',
  'Solution-Focused Therapy', 'Gestalt Therapy', 'Humanistic/Person-Centered',
  'Family Systems Therapy', 'Narrative Therapy', 'Cognitive Processing Therapy (CPT)',
  'Exposure Therapy', 'Internal Family Systems (IFS)', 'Somatic Therapy',
  'Art Therapy', 'Play Therapy', 'Group Therapy', 'Couples Therapy (Gottman Method)',
  'Emotionally Focused Therapy (EFT)'
];

const AGE_GROUPS_OPTIONS = [
  'Children (5-12)',
  'Adolescents (13-17)',
  'Young Adults (18-25)',
  'Adults (26-64)',
  'Seniors (65+)'
];

const SESSION_TYPES_OPTIONS = [
  'Individual Therapy',
  'Couples Therapy',
  'Family Therapy',
  'Group Therapy',
  'Intensive Outpatient Programs (IOP)',
  'Workshops/Seminars'
];

function PracticeDetailsForm({ 
  formData, 
  setFormData, 
  isEditing, 
  validationErrors = {}, 
  setValidationErrors = () => {}
}) {
  // Local state for insurance text input to prevent cursor jumping
  const [insuranceText, setInsuranceText] = useState('');

  // Initialize insurance text when component mounts or when formData changes
  useEffect(() => {
    if (formData.insuranceAccepted) {
      setInsuranceText(formData.insuranceAccepted.join(', '));
    }
  }, [formData.insuranceAccepted]);

  // Validation helper components
  const ValidationError = ({ error }) => {
    if (!error) return null;
    return (
      <div className="mt-1 flex items-center gap-1">
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-red-600 text-sm font-medium">{error}</p>
      </div>
    );
  };

  const FieldDescription = ({ children }) => (
    <p className="text-xs text-[#254e70]/60 mb-2">{children}</p>
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const nameParts = name.split('.');
      
      if (nameParts.length === 2) {
        // Handle two-level nesting like "rates.individual"
        const [parent, child] = nameParts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === 'checkbox' ? checked : value
          }
        }));
      } else if (nameParts.length === 3) {
        // Handle three-level nesting like "rates.freeConsultation.offered"
        const [parent, middle, child] = nameParts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [middle]: {
              ...prev[parent][middle],
              [child]: type === 'checkbox' ? checked : value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear validation error when user starts typing
    if (validationErrors[name] || validationErrors[name.split('.').pop()]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: '',
        [name.split('.').pop()]: ''
      }));
    }
  };

  // Helper function to handle checkbox arrays
  const handleCheckboxArrayChange = (field, value, checked) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      if (checked) {
        return {
          ...prev,
          [field]: [...currentArray, value]
        };
      } else {
        return {
          ...prev,
          [field]: currentArray.filter(item => item !== value)
        };
      }
    });

    // Clear validation error when user makes selection
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };


  // Handle insurance text input without cursor jumping
  const handleInsuranceTextChange = (e) => {
    setInsuranceText(e.target.value);
    
    // Clear validation error when user starts typing
    if (validationErrors.insuranceAccepted) {
      setValidationErrors(prev => ({
        ...prev,
        insuranceAccepted: ''
      }));
    }
  };

  // Capitalize first letter of each word in an insurance name
  const capitalizeInsuranceName = (name) => {
    return name.replace(/\b\w/g, char => char.toUpperCase());
  };

  // Parse insurance text into array when user finishes editing
  const handleInsuranceBlur = () => {
    const insuranceArray = insuranceText
      .split(/,|\n/)
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => capitalizeInsuranceName(item));
    setFormData(prev => ({
      ...prev,
      insuranceAccepted: insuranceArray
    }));
  };

  return (
    <div className="space-y-4">
      {/* Specializations */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Specializations
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional)</span>
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Select Your Specializations
            </label>
            <FieldDescription>
              Choose the mental health areas you specialize in treating
            </FieldDescription>
            {isEditing ? (
              <div>
                <div className={`grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3 ${
                  validationErrors.specializations ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                }`}>
                  {SPECIALIZATIONS_OPTIONS.map(specialization => (
                    <div key={specialization} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`specialization-${specialization}`}
                        checked={formData.specializations.includes(specialization)}
                        onChange={(e) => handleCheckboxArrayChange('specializations', specialization, e.target.checked)}
                        className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                      />
                      <label htmlFor={`specialization-${specialization}`} className="text-sm text-[#254e70]">
                        {specialization}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-start mt-1">
                  <ValidationError error={validationErrors.specializations} />
                  <p className="text-xs text-[#254e70]/60">
                    {formData.specializations.length} selected
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {formData.specializations.length > 0 ? 
                  formData.specializations.map(spec => (
                    <span key={spec} className="bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full text-sm">
                      {spec}
                    </span>
                  )) : 
                  <p className="text-[#254e70]/80">Not set</p>
                }
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Treatment Approaches */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Treatment Approaches
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional)</span>
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Select Your Treatment Approaches
            </label>
            <FieldDescription>
              Choose the therapeutic methods and techniques you use
            </FieldDescription>
            {isEditing ? (
              <div>
                <div className={`grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3 ${
                  validationErrors.treatmentApproaches ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                }`}>
                  {TREATMENT_APPROACHES_OPTIONS.map(approach => (
                    <div key={approach} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`approach-${approach}`}
                        checked={formData.treatmentApproaches.includes(approach)}
                        onChange={(e) => handleCheckboxArrayChange('treatmentApproaches', approach, e.target.checked)}
                        className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                      />
                      <label htmlFor={`approach-${approach}`} className="text-sm text-[#254e70]">
                        {approach}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-start mt-1">
                  <ValidationError error={validationErrors.treatmentApproaches} />
                  <p className="text-xs text-[#254e70]/60">
                    {formData.treatmentApproaches.length} selected
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {formData.treatmentApproaches.length > 0 ? 
                  formData.treatmentApproaches.map(approach => (
                    <span key={approach} className="bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full text-sm">
                      {approach}
                    </span>
                  )) : 
                  <p className="text-[#254e70]/80">Not set</p>
                }
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Age Groups & Session Types */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Age Groups & Session Types
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional)</span>
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Age Groups Served
            </label>
            <FieldDescription>
              Select the age groups you are qualified and comfortable working with
            </FieldDescription>
            {isEditing ? (
              <div>
                <div className={`grid grid-cols-1 gap-2 border rounded-lg p-3 ${
                  validationErrors.ageGroups ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                }`}>
                  {AGE_GROUPS_OPTIONS.map(ageGroup => (
                    <div key={ageGroup} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`age-${ageGroup}`}
                        checked={formData.ageGroups.includes(ageGroup)}
                        onChange={(e) => handleCheckboxArrayChange('ageGroups', ageGroup, e.target.checked)}
                        className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                      />
                      <label htmlFor={`age-${ageGroup}`} className="text-sm text-[#254e70]">
                        {ageGroup}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-start mt-1">
                  <ValidationError error={validationErrors.ageGroups} />
                  <p className="text-xs text-[#254e70]/60">
                    {formData.ageGroups.length} selected
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {formData.ageGroups.length > 0 ? 
                  formData.ageGroups.map(age => (
                    <span key={age} className="bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full text-sm">
                      {age}
                    </span>
                  )) : 
                  <p className="text-[#254e70]/80">Not set</p>
                }
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Session Types
            </label>
            <FieldDescription>
              Select the types of therapy sessions you offer
            </FieldDescription>
            {isEditing ? (
              <div>
                <div className={`grid grid-cols-1 gap-2 border rounded-lg p-3 ${
                  validationErrors.sessionTypes ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                }`}>
                  {SESSION_TYPES_OPTIONS.map(sessionType => (
                    <div key={sessionType} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`session-${sessionType}`}
                        checked={formData.sessionTypes.includes(sessionType)}
                        onChange={(e) => handleCheckboxArrayChange('sessionTypes', sessionType, e.target.checked)}
                        className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                      />
                      <label htmlFor={`session-${sessionType}`} className="text-sm text-[#254e70]">
                        {sessionType}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-start mt-1">
                  <ValidationError error={validationErrors.sessionTypes} />
                  <p className="text-xs text-[#254e70]/60">
                    {formData.sessionTypes.length} selected
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {formData.sessionTypes.length > 0 ? 
                  formData.sessionTypes.map(session => (
                    <span key={session} className="bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full text-sm">
                      {session}
                    </span>
                  )) : 
                  <p className="text-[#254e70]/80">Not set</p>
                }
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Session Rates */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Session Rates
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional)</span>
        </h3>
        <FieldDescription>
          Set your session rates to help clients understand your pricing structure
        </FieldDescription>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">Individual Session Rate ($)</label>
              {isEditing ? (
                <div>
                  <input
                    type="number"
                    name="rates.individual"
                    value={formData.rates.individual}
                    onChange={handleInputChange}
                    placeholder="150"
                    min="0"
                    step="5"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.individualRate ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                  />
                  <ValidationError error={validationErrors.individualRate} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">
                  {formData.rates.individual ? `$${formData.rates.individual}` : 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">Couples Session Rate ($)</label>
              {isEditing ? (
                <div>
                  <input
                    type="number"
                    name="rates.couples"
                    value={formData.rates.couples}
                    onChange={handleInputChange}
                    placeholder="180"
                    min="0"
                    step="5"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.couplesRate ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                  />
                  <ValidationError error={validationErrors.couplesRate} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">
                  {formData.rates.couples ? `$${formData.rates.couples}` : 'Not set'}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">Family Session Rate ($)</label>
              {isEditing ? (
                <div>
                  <input
                    type="number"
                    name="rates.family"
                    value={formData.rates.family}
                    onChange={handleInputChange}
                    placeholder="200"
                    min="0"
                    step="5"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.familyRate ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                  />
                  <ValidationError error={validationErrors.familyRate} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">
                  {formData.rates.family ? `$${formData.rates.family}` : 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">Group Session Rate ($)</label>
              {isEditing ? (
                <div>
                  <input
                    type="number"
                    name="rates.group"
                    value={formData.rates.group}
                    onChange={handleInputChange}
                    placeholder="75"
                    min="0"
                    step="5"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.groupRate ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                  />
                  <ValidationError error={validationErrors.groupRate} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">
                  {formData.rates.group ? `$${formData.rates.group}` : 'Not set'}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center bg-[#f8fafc] p-3 rounded-lg">
            <input
              type="checkbox"
              name="rates.slidingScale"
              checked={formData.rates.slidingScale}
              onChange={handleInputChange}
              disabled={!isEditing}
              className="mr-3 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
            />
            <div>
              <label className="text-sm font-medium text-[#254e70]">Offers sliding scale pricing</label>
              <p className="text-xs text-[#254e70]/60">Reduced rates based on financial need</p>
            </div>
          </div>
        </div>
      </div>

      {/* Complimentary Consultation Call */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Complimentary Consultation Call
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional)</span>
        </h3>
        <FieldDescription>
          Offering a brief consultation helps potential clients feel more comfortable before committing
        </FieldDescription>
        <div className="space-y-4">
          <div className="flex items-center bg-[#f8fafc] p-3 rounded-lg">
            <input
              type="checkbox"
              name="rates.freeConsultation.offered"
              checked={formData.rates.freeConsultation.offered}
              onChange={handleInputChange}
              disabled={!isEditing}
              className="mr-3 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
            />
            <div>
              <label className="text-sm font-medium text-[#254e70]">Offers complimentary consultation call</label>
              <p className="text-xs text-[#254e70]/60">Brief phone/video call to discuss needs and fit</p>
            </div>
          </div>
          {!isEditing && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-[#254e70]/80">
                {formData.rates.freeConsultation.offered 
                  ? '✅ Offers complimentary consultation calls to potential clients' 
                  : '❌ Does not offer complimentary consultation calls'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Availability */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Practice Availability
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional)</span>
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Client Availability
            </label>
            <FieldDescription>
              Let potential clients know if you are currently accepting new patients
            </FieldDescription>
            <div className="bg-[#f8fafc] p-3 rounded-lg">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="acceptingNewClients"
                  checked={formData.acceptingNewClients}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-3 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <div>
                  <label className="text-sm font-medium text-[#254e70]">
                    Currently accepting new clients
                  </label>
                  <p className="text-xs text-[#254e70]/60">
                    Check this if you have availability for new clients
                  </p>
                </div>
              </div>
            </div>
            <ValidationError error={validationErrors.acceptingNewClients} />
            {!isEditing && (
              <div className="mt-2 p-3 rounded-lg bg-gray-50">
                <p className={`text-sm font-medium ${formData.acceptingNewClients ? 'text-green-700' : 'text-amber-700'}`}>
                  {formData.acceptingNewClients ? '✅ Accepting new clients' : '⏸️ Not currently accepting new clients'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Insurance & Payment */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Insurance & Payment
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional but Recommended)</span>
        </h3>
        <FieldDescription>
          Help clients understand what insurance you accept and payment options available
        </FieldDescription>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">
              Insurance Networks
              <span className="text-xs text-[#254e70]/60 ml-1">(separate with commas)</span>
            </label>
            <FieldDescription>
              List the insurance networks you are in-network with
            </FieldDescription>
            {isEditing ? (
              <div>
                <textarea
                  name="insuranceAccepted"
                  value={insuranceText}
                  onChange={handleInsuranceTextChange}
                  onBlur={handleInsuranceBlur}
                  placeholder="Aetna, Blue Cross Blue Shield, Cigna, UnitedHealth, etc."
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] h-20 ${
                    validationErrors.insuranceAccepted ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}
                />
                <div className="flex justify-between items-start mt-1">
                  <ValidationError error={validationErrors.insuranceAccepted} />
                  <p className="text-xs text-[#254e70]/60">
                    Enter each insurance network separated by commas
                  </p>
                </div>
                {formData.insuranceAccepted.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {formData.insuranceAccepted.map((insurance, index) => (
                      <span 
                        key={index} 
                        className="inline-block px-2 py-1 bg-[#9dacff]/10 text-[#254e70] rounded text-xs"
                      >
                        {insurance}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="py-2 text-[#254e70]/80">
                  {formData.insuranceAccepted.length > 0 
                    ? formData.insuranceAccepted.join(', ') 
                    : 'Not specified'
                  }
                </p>
                {formData.insuranceAccepted.length > 0 && (
                  <p className="text-xs text-[#254e70]/60">
                    {formData.insuranceAccepted.length} insurance network{formData.insuranceAccepted.length > 1 ? 's' : ''} accepted
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-[#254e70]">Insurance Options</h4>
            <FieldDescription>
              Select all that apply to your practice
            </FieldDescription>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  name="insurance.acceptsInsurance"
                  checked={formData.insurance.acceptsInsurance}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Accepts Insurance</label>
              </div>
              <div className="flex items-center p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  name="insurance.outOfNetwork"
                  checked={formData.insurance.outOfNetwork}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Out-of-Network</label>
              </div>
              <div className="flex items-center p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  name="insurance.providesSuperbill"
                  checked={formData.insurance.providesSuperbill}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Provides Superbill</label>
              </div>
              <div className="flex items-center p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  name="insurance.priorAuthRequired"
                  checked={formData.insurance.priorAuthRequired}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Prior Auth Required</label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-[#254e70]">Payment Methods</h4>
            <FieldDescription>
              Select the payment methods you accept
            </FieldDescription>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  name="paymentMethods.acceptsCashCheck"
                  checked={formData.paymentMethods.acceptsCash || formData.paymentMethods.acceptsCheck}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      paymentMethods: {
                        ...prev.paymentMethods,
                        acceptsCash: checked,
                        acceptsCheck: checked
                      }
                    }));
                  }}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Cash/Check</label>
              </div>
              <div className="flex items-center p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  name="paymentMethods.acceptsCards"
                  checked={formData.paymentMethods.acceptsVisa || formData.paymentMethods.acceptsMastercard || formData.paymentMethods.acceptsAmericanExpress || formData.paymentMethods.acceptsDiscover}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      paymentMethods: {
                        ...prev.paymentMethods,
                        acceptsVisa: checked,
                        acceptsMastercard: checked,
                        acceptsAmericanExpress: checked,
                        acceptsDiscover: checked
                      }
                    }));
                  }}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Debit/Credit Cards</label>
              </div>
              <div className="flex items-center p-2 bg-gray-50 rounded">
                <input
                  type="checkbox"
                  name="paymentMethods.acceptsHSAFSA"
                  checked={formData.paymentMethods.acceptsHSA || formData.paymentMethods.acceptsFSA}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({
                      ...prev,
                      paymentMethods: {
                        ...prev.paymentMethods,
                        acceptsHSA: checked,
                        acceptsFSA: checked
                      }
                    }));
                  }}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">HSA/FSA</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PracticeDetailsForm; 